import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Navigate, useLocation } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Typography,
  Button,
  Stack,
  TextField,
  InputAdornment,
  IconButton,
  Card,
  CardContent,
} from "@mui/material";
import { VisibilityOff, Visibility } from "@mui/icons-material";
import { resetUserPassword, logout } from "features/auth/auth.slice";
import { readAsyncStorageValues } from "features/service/service.slice";
import { Links } from "utils/pageLinkNames";

const ResetPassword = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { token, user } = useSelector((state) => state.auth);
  const { appConstants } = useSelector((state) => state.service);
  const resetStatus = useSelector((state) => state.auth.resetPasswordStatus);
  const [resetPasswordObj, setResetPasswordObj] = useState({
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState({
    password: false,
    confirmPassword: false,
  });
  const [errors, setErrors] = useState({
    password: "",
    confirmPassword: "",
  });

  // useEffect(() => {
  //   if (appConstants?.resetPassword === undefined) {
  //     navigate(Links.PLANT_OVERVIEW, { replace: true });
  //   }
  // }, [appConstants?.resetPassword, navigate ]);

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setResetPasswordObj(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors = {
      password: "",
      confirmPassword: "",
    };

    if (!resetPasswordObj.password.trim()) {
      newErrors.password = "Password is required";
      isValid = false;
    } else if (resetPasswordObj.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
      isValid = false;
    }

    if (!resetPasswordObj.confirmPassword.trim()) {
      newErrors.confirmPassword = "Confirm Password is required";
      isValid = false;
    } else if (resetPasswordObj.password !== resetPasswordObj.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleResetPassword = () => {
    if (!token) {
      dispatch(logout());
      navigate(Links.LOGIN, { replace: true });
      return;
    }

    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    dispatch(resetUserPassword(resetPasswordObj))
      .unwrap()
      .then(() => {
        toast.success("Password reset successfully!");
        dispatch(readAsyncStorageValues());
      })
      .then (() => {
        // if (user.roles.includes(appConstants?.allTypes?.USER_ROLES?.ROLE_MAINTENANCE_SUPERVISOR)) {
        //   return <Navigate to={`/${Links.PLANT_OVERVIEW}`} state={{ from: location }} replace />;
        // } else if (user.roles.includes(appConstants?.allTypes?.USER_ROLES?.ROLE_FINANCE)) {
        //   return <Navigate to={`/${Links.ASSETS}`} state={{ from: location }} replace />;
        // } else if (user.roles.includes(appConstants?.allTypes?.USER_ROLES?.ROLE_STORES)) {
        //   return <Navigate to={`/${Links.SPARES_REQUIRED}`} state={{ from: location }} replace />;
        // } else if (user.roles.includes(appConstants?.allTypes?.USER_ROLES?.ROLE_SERVICE_TECHNICIAN)) {
        //   return <Navigate to={`/${Links.ALL_SERVICE_TICKETS}`} state={{ from: location }} replace />;
        // } else {
        //   return <Navigate to={`/${Links.USER}`} state={{ from: location }} replace />;
        // }
        if (user.roles.includes(appConstants?.allTypes?.USER_ROLES?.ROLE_MAINTENANCE_SUPERVISOR)) {
          navigate(`/${Links.PLANT_OVERVIEW}`, { replace: true });
        } else if (user.roles.includes(appConstants?.allTypes?.USER_ROLES?.ROLE_FINANCE)) {
          navigate(`/${Links.ASSETS}`, { replace: true });
        } else if (user.roles.includes(appConstants?.allTypes?.USER_ROLES?.ROLE_STORES)) {
          navigate(`/${Links.SPARES_REQUIRED}`, { replace: true });
        } else if (user.roles.includes(appConstants?.allTypes?.USER_ROLES?.ROLE_SERVICE_TECHNICIAN)) {
          navigate(`/${Links.ALL_SERVICE_TICKETS}`, { replace: true });
        } else {
          navigate(`/${Links.USER}`, { replace: true });
        }
      })
      .catch((error) => {
        toast.error(error.message || "Failed to reset password");
      });
  };

  return (
    <Stack
      justifyContent="center"
      alignItems="center"
      sx={{
        minHeight: "100vh",
        p: 2,
        backgroundColor: (theme) =>
          theme.palette.mode === "light"
            ? theme.palette.grey[100]
            : theme.palette.background.default,
      }}
    >
      <Card sx={{ maxWidth: 500, width: "100%", p: 3 }}>
        <CardContent>
          <Typography
            variant="h5"
            component="h1"
            align="center"
            gutterBottom
            sx={{ mb: 3, fontWeight: "bold", color: "primary.main" }}
          >
            Reset Password
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Password must contain at least 6 characters.
          </Typography>

          <TextField
            fullWidth
            label="Password"
            name="password"
            type={showPassword.password ? "text" : "password"}
            value={resetPasswordObj.password}
            onChange={handlePasswordChange}
            error={!!errors.password}
            helperText={errors.password}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() =>
                      setShowPassword({
                        ...showPassword,
                        password: !showPassword.password,
                      })
                    }
                    edge="end"
                  >
                    {showPassword.password ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Confirm Password"
            name="confirmPassword"
            type={showPassword.confirmPassword ? "text" : "password"}
            value={resetPasswordObj.confirmPassword}
            onChange={handlePasswordChange}
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() =>
                      setShowPassword({
                        ...showPassword,
                        confirmPassword: !showPassword.confirmPassword,
                      })
                    }
                    edge="end"
                  >
                    {showPassword.confirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 3 }}
          />

          <Button
            fullWidth
            variant="contained"
            onClick={handleResetPassword}
            size="large"
          >
            Reset Password
          </Button>
        </CardContent>
      </Card>
      <ToastContainer />
    </Stack>
  );
};

export default ResetPassword;